/**
 * useContentBuckets — reads bucket rows + a per-sub-category reference count
 * for the Buckets tab. Keeps a light footprint; Composer loads its own richer
 * per-bucket view.
 */
import { useQuery } from '@tanstack/react-query';
import { marketing } from '@/lib/supabase/marketing';

export interface ContentBucket {
  slug: string;
  name: string;
  description: string | null;
  drive_bucket_folder_id: string | null;
  style_prompt: string | null;
  mood: string | null;
  brand_voice_notes: string | null;
  color_accent: string | null;
  target_monthly: number | null;
  is_suggested: boolean;
}

export interface BucketWithRefs extends ContentBucket {
  ref_count: number;
  sub_category_counts: Array<{ sub_category: string | null; count: number }>;
}

async function fetchBuckets(): Promise<BucketWithRefs[]> {
  const { data: buckets, error } = await marketing
    .from('content_bucket')
    .select('slug,name,description,drive_bucket_folder_id,style_prompt,mood,brand_voice_notes,color_accent,target_monthly,is_suggested')
    .eq('is_active', true)
    .order('slug');
  if (error) throw error;

  const { data: refs, error: refErr } = await marketing
    .from('content_bucket_reference')
    .select('bucket_slug,sub_category');
  if (refErr) throw refErr;

  const byBucket = new Map<string, Map<string | null, number>>();
  for (const r of refs ?? []) {
    const m = byBucket.get(r.bucket_slug) ?? new Map();
    m.set(r.sub_category ?? null, (m.get(r.sub_category ?? null) ?? 0) + 1);
    byBucket.set(r.bucket_slug, m);
  }

  return (buckets ?? []).map((b) => {
    const m = byBucket.get(b.slug) ?? new Map();
    const sub_category_counts = Array.from(m.entries())
      .map(([sub_category, count]) => ({ sub_category, count }))
      .sort((a, b) => (b.count as number) - (a.count as number));
    const ref_count = sub_category_counts.reduce((s, r) => s + (r.count as number), 0);
    return { ...(b as ContentBucket), ref_count, sub_category_counts };
  });
}

export function useContentBuckets() {
  return useQuery<BucketWithRefs[]>({
    queryKey: ['content-buckets'],
    queryFn: fetchBuckets,
    staleTime: 30_000,
  });
}
